import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  Button,
  Col,
  Form,
  FormControl,
  FormLabel,
  Row,
  Spinner,
} from 'react-bootstrap'
import { RiCloseLine } from 'react-icons/ri'
import { useSelector } from 'react-redux'
import swal from 'sweetalert'
import CustomDropzone from '../CustomDropzone.jsx'
import { useUploadADHDFileMutation } from '../../redux/api/brainApi.js'

// Validation Schema
const validationSchema = Yup.object().shape({
  mins: Yup.number()
    .min(0, 'Minutes must be greater than or equal to 0')
    .max(60, 'Minutes must be less than or equal to 60')
    .required('Required'),
  secs: Yup.number()
    .min(0, 'Seconds must be greater than or equal to 0')
    .max(59, 'Seconds must be less than 60')
    .required('Required'),
  file: Yup.mixed()
    .required('A file is required')
    .test('filePresent', 'A file is required', (value) => value !== null),
})

const FileUploadForm = () => {
  const { selectedUser } = useSelector((state) => state.user)
  const [uploadADHDFile, { isLoading }] = useUploadADHDFileMutation()

  const initialValues = {
    mins: 0,
    secs: 30,
    file: null,
  }

  const handleSubmit = async (values, { setFieldError, resetForm }) => {
    try {
      await uploadADHDFile({
        file: values.file,
        selectedUser,
        mins: values.mins,
        secs: values.secs,
      }).unwrap()

      await swal({
        title: 'Success',
        text: 'Uploaded Successfully, Switch to EEG report for updated results',
        icon: 'success',
      })

      resetForm()
    } catch (err) {
      setFieldError(
        'form',
        err?.data?.error || 'An error occurred during upload'
      )
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  })

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row>
        <Col>
          <Form.Group className='mb-3'>
            <FormLabel>Minutes</FormLabel>
            <FormControl
              type='number'
              name='mins'
              min={0}
              max={60}
              value={formik.values.mins}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.mins && !!formik.errors.mins}
            />
            <Form.Control.Feedback type='invalid'>
              {formik.errors.mins}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className='mb-3'>
            <FormLabel>Seconds</FormLabel>
            <FormControl
              type='number'
              name='secs'
              min={0}
              max={59}
              value={formik.values.secs}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.secs && !!formik.errors.secs}
            />
            <Form.Control.Feedback type='invalid'>
              {formik.errors.secs}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group>
        <FormLabel>Files</FormLabel>
        <CustomDropzone
          isMultiple={false}
          onSubmit={(file) => {
            formik.setFieldValue('file', file)
            formik.setFieldTouched('file', true, false) // Add false to prevent validation
            formik.setErrors({ ...formik.errors, file: undefined }) // Clear file error
          }}
          loading={isLoading}
        />
        {formik.touched.file && formik.errors.file && (
          <div className='text-danger mt-1' style={{ fontSize: '0.875rem' }}>
            {formik.errors.file}
          </div>
        )}
      </Form.Group>

      {formik.values.file && (
        <FileDisplay
          file={formik.values.file}
          onRemove={() => formik.setFieldValue('file', null)}
          disabled={isLoading}
        />
      )}

      {formik.errors.form && (
        <div className='text-danger mb-3'>{formik.errors.form}</div>
      )}

      <Button
        type='submit'
        className='border-0 common-btn w-100 py-3 mt-2'
        disabled={isLoading}
      >
        {isLoading ? (
          <div className='d-flex align-items-center justify-content-center gap-2'>
            <Spinner animation='border' size='sm' />
            Uploading...
          </div>
        ) : (
          'Upload'
        )}
      </Button>
    </Form>
  )
}

export default FileUploadForm

const FileDisplay = ({ file, onRemove, disabled }) => (
  <div
    className='d-flex justify-content-between align-items-center p-3 rounded-3 my-2'
    style={{ backgroundColor: '#f6f6f6' }}
  >
    <div>
      <p className='fw-bold m-0' style={{ wordBreak: 'break-word' }}>
        {file.name}
      </p>
      <p className='text-secondary m-0'>
        {(file.size / 1024 / 1024).toFixed(2)} MB
      </p>
    </div>
    <button
      className='border-0 rounded-circle bg-transparent'
      onClick={onRemove}
      disabled={disabled}
    >
      <RiCloseLine size={24} />
    </button>
  </div>
)
